import * as React from "react";
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    NumberInput,
    ReferenceArrayInput,
    SelectArrayInput,
    ReferenceInput,
    SelectInput,
    ImageField,
    ImageInput,
    useGetList,
    useLocaleState,
} from 'react-admin';
import { useWatch, useFormContext } from 'react-hook-form';
import { DisplayStatus } from '../../shared/enums/display-status.enum';
import { TimePeriod } from '../../shared/interfaces/timePeriod.interface';
import GetTimePeriodRange from '../../shared/services/timePeriod.services';
import { locales } from '../../i18n/languges';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues } from 'react-hook-form';

// Helper function to safely get nested translated field
const getTranslatedField = (record: any, field: string, locale: string): string => {
    if (!record) return '';
    const parts = field.split('.');
    let value = record;
    for (const part of parts) {
        if (value && typeof value === 'object' && part in value) {
            value = value[part];
        } else {
            return '';
        }
    }
    if (value == null) return '';
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        if (value[locale]) return String(value[locale]);
        for (const l of locales) {
            if (value[l]) return String(value[l]);
        }
        const first = Object.values(value).find(v => v != null && v !== '');
        return first ? String(first) : '';
    }
    if (typeof value === 'string') return value;
    return String(value);
};

export const PoiCreate = () => {
    const [locale] = useLocaleState();
    const [selectedLocale, setSelectedLocale] = useState(locale || locales[0]);
    const timePeriods = useGetList<TimePeriod>(
        "timeperiods",
        {
            pagination: { page: 1, perPage: 100 },
            sort: { field: "id", order: "ASC" },
        },
    );
    const [displayValue, setDisplayValue] = useState(DisplayStatus.Display);
    const [selectedTimePeriodId, setSelectedTimePeriodId] = useState<number>(0);
    const selectedTimePeriod = GetTimePeriodRange(
        selectedTimePeriodId,
        timePeriods.data || {}
    );
    const displayOptions = Object.keys(DisplayStatus).map(key => ({
        id: DisplayStatus[key],
        name: `resources.enums.displayStatus.${key}`,
    }));

    const schema = yup.object().shape({
        latitude: yup.number()
            .required('Latitude is required')
            .min(-90, 'Latitude must be at least -90')
            .max(90, 'Latitude must be at most 90'),
        longitude: yup.number()
            .required('Longitude is required')
            .min(-180, 'Longitude must be at least -180')
            .max(180, 'Longitude must be at most 180'),
        pointOfInterestCategoryId: yup.string().required('Point of Interest Category is required'),
        display: yup.mixed().required('Display status is required'),
        name: yup.object().shape({
            en: yup.string().when('$pointOfInterestCategoryId', {
                is: (val: any) => val != 19,
                then: schema => schema.required('Name in English is required'),
                otherwise: schema => schema.nullable()
            }),
        }).nullable(),
        elevation: yup.number().nullable()
            .transform((_value, originalValue) => {
                if (originalValue === '' || originalValue === null || originalValue === undefined) return null;
                return Number(originalValue);
            }),
        timePeriodId: yup.string().nullable().when('display', {
            is: (value: string | undefined) => value === DisplayStatus.Scheduled,
            then: schema => schema.required('Time period can\'t be empty if display is scheduled'),
            otherwise: schema => schema.nullable()
        }),
    });
    const resolver = yupResolver<FieldValues>(schema);

    // Helper: renders a TextInput for ALL locales but only shows the selected one.
    const TField = ({ source, ...props }: { source: string; [key: string]: any }) => (
        <Box sx={{ width: '100%' }}>
            {locales.map(l => (
                <Box key={`${source}.${l}`} sx={{ display: selectedLocale === l ? undefined : 'none' }}>
                    <TextInput source={`${source}.${l}`} fullWidth {...props} />
                </Box>
            ))}
        </Box>
    );

    // Section header with divider line
    const SectionHeader = ({ label }: { label: string }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', mt: 2, mb: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontSize: '1.1rem', fontWeight: 700, whiteSpace: 'nowrap', color: 'text.secondary' }}>
                {label}
            </Typography>
            <Divider sx={{ flexGrow: 1, borderBottomWidth: 2 }} />
        </Box>
    );

    const FormContent = () => {
        const { control } = useFormContext();
        const categoryId = useWatch({ name: 'pointOfInterestCategoryId', control });
        const isTemperaturePoi = categoryId == 19;

        if (isTemperaturePoi) {
            return (
                <>
                    {/* Temperature POI: simplified layout */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, width: '100%' }}>
                        <ReferenceInput source="pointOfInterestCategoryId" reference="poicategories">
                            <SelectInput optionText={(record) => getTranslatedField(record, 'name', selectedLocale)} label="Category" fullWidth />
                        </ReferenceInput>
                        <SelectInput source="display" choices={displayOptions} defaultValue={DisplayStatus.Display} fullWidth />
                    </Box>

                    <SectionHeader label="Position" />
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, width: '100%' }}>
                        <NumberInput source="latitude" label="Latitude" min={-90} max={90} fullWidth />
                        <NumberInput source="longitude" label="Longitude" min={-180} max={180} fullWidth />
                        <NumberInput
                            source="description.temperature"
                            label="Temperatur (°C)"
                            step={0.1}
                            helperText="Aktuell temperatur i grader Celsius"
                            defaultValue={0}
                            fullWidth
                        />
                    </Box>
                    {/* Hidden fields with default values to pass validation */}
                    <TextInput source="name.en" defaultValue="Temperature Station" sx={{ display: 'none' }} />
                    <NumberInput source="elevation" defaultValue={0} sx={{ display: 'none' }} />
                </>
            );
        }

        return (
            <>
                {/* ── Language tabs ── */}
                <Tabs
                    value={selectedLocale}
                    onChange={(_, v) => setSelectedLocale(v)}
                    sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', width: '100%' }}
                >
                    {locales.map(l => (
                        <Tab key={l} value={l} label={l.toUpperCase()} />
                    ))}
                </Tabs>

                {/* ── Row 1: Name + Category (2 cols) ── */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, width: '100%' }}>
                    <TField source="name" label="Name" />
                    <ReferenceInput source="pointOfInterestCategoryId" reference="poicategories">
                        <SelectInput optionText={(record) => getTranslatedField(record, 'name', selectedLocale)} label="Category" fullWidth />
                    </ReferenceInput>
                </Box>

                {/* ══ STATUS SECTION ══ */}
                <SectionHeader label="Status" />
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, width: '100%' }}>
                    <Box>
                        <SelectInput
                            source="display"
                            choices={displayOptions}
                            defaultValue={DisplayStatus.Display}
                            fullWidth
                            onChange={(e) => setDisplayValue(e.target.value as DisplayStatus)}
                        />
                        {displayValue === DisplayStatus.Scheduled && (
                            <ReferenceInput
                                source="timePeriodId"
                                reference="timeperiods"
                                onChange={(e) => setSelectedTimePeriodId(e.target.value)}
                            >
                                <SelectInput
                                    optionText="name"
                                    label="Time period"
                                    helperText={selectedTimePeriod}
                                    fullWidth
                                />
                            </ReferenceInput>
                        )}
                    </Box>
                    <ReferenceArrayInput source="area_ids" reference="areas">
                        <SelectArrayInput optionText={(record) => getTranslatedField(record, 'name', selectedLocale)} label="Areas" defaultValue={[]} fullWidth />
                    </ReferenceArrayInput>
                </Box>

                {/* ══ POSITION SECTION ══ */}
                <SectionHeader label="Position" />
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, width: '100%' }}>
                    <NumberInput source="latitude" min={-90} max={90} fullWidth />
                    <NumberInput source="longitude" min={-180} max={180} fullWidth />
                    <NumberInput source="elevation" fullWidth />
                </Box>

                {/* ══ INFORMATION SECTION ══ */}
                <SectionHeader label="Information" />
                <TextInput source="url" fullWidth />
                <TField source="description" rows={8} multiline />

                {/* ══ FILES SECTION ══ */}
                <SectionHeader label="Files" />
                <ImageInput source="medias" multiple={true} label="Medias" fullWidth>
                    <ImageField source="src" title="title" />
                </ImageInput>

                {/* ══ SOCIAL MEDIA SECTION ══ */}
                <SectionHeader label="Social media links" />
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, width: '100%' }}>
                    <TextInput source="youtubeVideoId" label="YouTube id" fullWidth />
                    <TextInput source="socialMedia.instagramUrl" label="Instagram url" fullWidth />
                    <TextInput source="socialMedia.facebookUrl" label="Facebook url" fullWidth />
                    <TextInput source="socialMedia.linkedinUrl" label="Linkedin url" fullWidth />
                    <TextInput source="socialMedia.twitterUrl" label="Twitter url" fullWidth />
                    <TextInput source="socialMedia.vimeoUrl" label="Vimeo url" fullWidth />
                    <TextInput source="socialMedia.youtubeUrl" label="Youtube url" fullWidth />
                    <TextInput source="socialMedia.tripadvisorUrl" label="Tripadvisor url" fullWidth />
                </Box>
            </>
        );
    };

    return (
        <Create>
            <SimpleForm sanitizeEmptyValues resolver={resolver}>
                <FormContent />
            </SimpleForm>
        </Create>
    );
}